<script>
import SalesService from "@services/resources/SalesService";
import access from "@src/mixins/access";
import EmailModal from "../components/modals/EmailModal.vue";
import RefundSaleModal from "../components/modals/RefundSaleModal.vue";
import CancelSubscriptionModal from "../components/modals/CancelSubscriptionModal.vue";
import Button from "../components/Button.vue";
import { AccessLevel } from "@views/configurations/enums/accessLevel";
import {
  canRequestRefund,
  getBackendRefundDisabledReason,
  getRefundRequestErrorMessage,
} from "../refundEligibility";
import CancelSubscriptionIcon from "@src/shared/icons/cancel-subscription.svg";
import RefundIcon from "@src/shared/icons/refund.svg";

const service = SalesService.build();

const CANCELLATION_FALLBACK_ERROR =
  "Não foi possível iniciar o cancelamento. Tente novamente.";

function getCancellationErrorMessage(error) {
  const response = error && error.response;
  const data = (response && response.data) || {};
  const validationErrors = data.errors || data;

  if (data.error && typeof data.error.message === "string") {
    return data.error.message;
  }

  if (typeof data.error === "string") return data.error;

  for (const field of ["subscription", "confirmation"]) {
    const message = validationErrors[field];

    if (Array.isArray(message) && typeof message[0] === "string") {
      return message[0];
    }

    if (typeof message === "string") return message;
  }

  if (typeof data.message === "string") return data.message;

  return CANCELLATION_FALLBACK_ERROR;
}

export default {
  mixins: [access],
  emits: ["reload"],
  components: {
    Button,
    EmailModal,
    RefundSaleModal,
    CancelSubscriptionModal,
  },
  props: {
    sale: {
      type: Object,
      required: false,
      default: () => ({}),
    },
  },
  computed: {
    canChangeEmail() {
      if (!this.useActionPermissions) {
        return true;
      }

      return this.$canAction("sales_change_email", AccessLevel.EDITOR);
    },
    canDownloadRefundReceipt() {
      if (!this.useActionPermissions) {
        return true;
      }

      return this.$canAction("sales_refund_receipt_button", AccessLevel.EDITOR);
    },
    canRefundSale() {
      return canRequestRefund(this.$store);
    },
    canCancelSubscription() {
      if (!this.useActionPermissions) return true;

      return this.$canAction(
        "sales_next_cancel_subscription_button",
        AccessLevel.EDITOR
      );
    },
    isTransaction() {
      return this.sale.type === "TRANSACTION";
    },
    isSubscription() {
      return this.sale.type === "SUBSCRIPTION";
    },
    isContract() {
      return this.sale.type === "CONTRACT";
    },
    isRecurringSale() {
      return this.isSubscription || this.isContract;
    },
    hasActiveClientContract() {
      const status =
        this.sale.client_contract && this.sale.client_contract.status;

      return !!status && status !== "canceled" && status !== "ended";
    },
    isCancellationProcessing() {
      return this.sale.client_contract
        && this.sale.client_contract.status === "processing";
    },
    cancelLabel() {
      return this.isContract ? "Cancelar contrato" : "Cancelar assinatura";
    },
    refundDisabledReason() {
      return getBackendRefundDisabledReason({
        hasPermission: this.canRefundSale,
        canRefund: this.sale.can_refund,
        refundBlockedReason: this.sale.refund_blocked_reason,
        status: this.sale.status,
      });
    },
    isRefundDisabled() {
      return !!this.refundDisabledReason;
    },
    cancelDisabledReason() {
      if (!this.canCancelSubscription) return "Sem permissão para cancelar";
      if (this.isCancellationProcessing) return "Cancelamento em processamento.";

      return "";
    },
    isCancelDisabled() {
      return !!this.cancelDisabledReason;
    },
  },
  data() {
    return {
      cancelSubscriptionIcon: CancelSubscriptionIcon,
      refundIcon: RefundIcon,
      refundLoading: false,
      refundErrorMessage: "",
      cancelLoading: false,
      cancelErrorMessage: "",
      downloading: false,
    };
  },
  methods: {
    async downloadPdf() {
      if (this.downloading) {
        return;
      }

      this.downloading = true;

      try {
        const pdf = await service.getRefundReceipt(this.$route.params.id);

        const byteCharacters = atob(pdf);
        const byteNumbers = Array.from(byteCharacters, (char) => char.charCodeAt(0));
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: "application/pdf" });
        const link = document.createElement("a");

        const url = URL.createObjectURL(blob);

        link.href = url;
        link.download = "comprovante-de-reembolso.pdf";
        link.click();

        setTimeout(() => URL.revokeObjectURL(url), 1000);
      } catch (e) {
        this.$bvToast.toast("Não foi possível baixar o comprovante de reembolso.", {
          title: "Erro ao baixar o comprovante!",
          variant: "danger",
          autoHideDelay: 5000,
          appendToast: true,
        });
      } finally {
        this.downloading = false;
      }
    },
    handleEmailModal() {
      this.$bvModal.show("email-modal");
    },
    showRefundModal() {
      this.$bvModal.show("refund-sale-modal");
    },
    async refundSale() {
      if (this.refundLoading) return;

      this.refundLoading = true;
      this.refundErrorMessage = "";

      try {
        await service.refund(this.sale.id, "REEMBOLSAR VENDA");

        this.$bvToast.toast("Solicitação de reembolso iniciada.", {
          title: "Solicitação enviada!",
          variant: "success",
          autoHideDelay: 5000,
          appendToast: true,
        });

        this.$bvModal.hide("refund-sale-modal");
        this.$emit("reload");
      } catch (error) {
        this.refundErrorMessage = getRefundRequestErrorMessage(error);
      } finally {
        this.refundLoading = false;
      }
    },
    clearRefundError() {
      this.refundErrorMessage = "";
    },
    showCancelSubscriptionModal() {
      this.$bvModal.show("cancel-subscription-modal");
    },
    async cancelSubscription() {
      if (this.cancelLoading) return;

      const clientContractId = this.sale.client_contract && this.sale.client_contract.id;

      if (!clientContractId) {
        this.cancelErrorMessage = "Não foi possível identificar a assinatura ou contrato.";
        return;
      }

      this.cancelLoading = true;
      this.cancelErrorMessage = "";

      try {
        await service.cancelSubscription(
          clientContractId,
          this.sale.type === "CONTRACT"
            ? "CANCELAR CONTRATO"
            : "CANCELAR ASSINATURA"
        );

        this.$bvToast.toast("Solicitação de cancelamento iniciada.", {
          title: "Solicitação enviada!",
          variant: "success",
          autoHideDelay: 5000,
          appendToast: true,
        });

        this.$bvModal.hide("cancel-subscription-modal");
        this.$emit("reload");
      } catch (error) {
        this.cancelErrorMessage = getCancellationErrorMessage(error);
      } finally {
        this.cancelLoading = false;
      }
    },
    clearCancelError() {
      this.cancelErrorMessage = "";
    },
  },
};
</script>

<template>
  <div class="sales-actions">
    <Button
      v-if="isTransaction"
      :disabled="isRefundDisabled"
      :tooltip="refundDisabledReason"
      label="Reembolsar"
      :icon-src="refundIcon"
      @click="showRefundModal"
    />

    <Button
      v-if="isRecurringSale && hasActiveClientContract"
      :disabled="isCancelDisabled"
      :tooltip="cancelDisabledReason"
      :label="cancelLabel"
      :icon-src="cancelSubscriptionIcon"
      @click="showCancelSubscriptionModal"
    />

    <Button
      v-if="canChangeEmail"
      label="Alterar e-mail"
      icon="far fa-envelope"
      @click="handleEmailModal"
    />

    <Button
      v-if="sale.status === 'refunded' && canDownloadRefundReceipt"
      label="Comprovante"
      icon="fas fa-download"
      :disabled="downloading"
      @click="downloadPdf"
    />

    <EmailModal :sale="sale" @reload="$emit('reload')" />
    <RefundSaleModal
      :sale="sale"
      :loading="refundLoading"
      :error-message="refundErrorMessage"
      @confirm="refundSale"
      @hidden="clearRefundError"
    />
    <CancelSubscriptionModal
      :sale="sale"
      :loading="cancelLoading"
      :error-message="cancelErrorMessage"
      @confirm="cancelSubscription"
      @hidden="clearCancelError"
    />
  </div>
</template>

<style scoped>
.sales-actions {
  gap: 1rem;
  display: flex;
  padding: 0 1.5rem;
}
</style>
